import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiCheck,
  FiChevronDown,
  FiGrid,
  FiList,
  FiSearch,
  FiSliders,
  FiX,
} from "react-icons/fi";
import * as D from "./Farms.desktop.styled";

const DEFAULT_QUICK_REGIONS = ["all", "central", "north", "south", "coastal"];

const FarmsCommandBar = ({
  searchQuery,
  onSearchQueryChange,
  sortOption,
  onSortChange,
  sortOptions,
  viewMode,
  onViewModeChange,
  filters,
  onRegionSelect,
  onOrganicToggle,
  regionOptions,
  activeFilterChips,
  activeFilterCount,
  onOpenFilters,
  onRemoveChip,
  onResetFilters,
}) => {
  const { t } = useTranslation();
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!sortOpen) return undefined;
    const handleDown = (event) => {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDown);
    return () => document.removeEventListener("mousedown", handleDown);
  }, [sortOpen]);

  useEffect(() => {
    const handleKey = (event) => {
      const key = event.key ? event.key.toLowerCase() : "";
      if (key === "/" && document.activeElement !== searchRef.current) {
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea") return;
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (key === "escape" && document.activeElement === searchRef.current) {
        searchRef.current?.blur();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const quickRegions = useMemo(
    () =>
      regionOptions.filter((option) => DEFAULT_QUICK_REGIONS.includes(option.value)),
    [regionOptions]
  );

  const sortLabel = useMemo(
    () =>
      sortOptions.find((option) => option.value === sortOption)?.label ??
      sortOptions[0]?.label,
    [sortOptions, sortOption]
  );

  return (
    <D.CommandBar>
      <D.CommandTopRow>
        <D.SearchField>
          <FiSearch className="leading" aria-hidden="true" size={18} />
          <D.SearchInput
            ref={searchRef}
            type="search"
            value={searchQuery}
            onChange={(event) => onSearchQueryChange(event.target.value)}
            placeholder={t("farms.search_placeholder", {
              defaultValue: "Search farms, regions, or tags…",
            })}
            aria-label={t("farms.search_placeholder", {
              defaultValue: "Search farms…",
            })}
          />
          {searchQuery ? (
            <D.SearchClear
              type="button"
              onClick={() => onSearchQueryChange("")}
              aria-label={t("farms.search_clear", {
                defaultValue: "Clear search",
              })}
            >
              <FiX size={14} aria-hidden="true" />
            </D.SearchClear>
          ) : (
            <D.KbdHint aria-hidden="true">/</D.KbdHint>
          )}
        </D.SearchField>

        <D.FiltersButton
          type="button"
          onClick={onOpenFilters}
          $count={activeFilterCount}
          aria-label={t("farms.filters.open", { defaultValue: "Filters" })}
        >
          <FiSliders aria-hidden="true" />
          {t("farms.filters.open", { defaultValue: "Filters" })}
          {activeFilterCount > 0 ? (
            <span className="badge">{activeFilterCount}</span>
          ) : null}
        </D.FiltersButton>

        <D.SortMenu ref={sortRef}>
          <D.SortButton
            type="button"
            $open={sortOpen}
            onClick={() => setSortOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={sortOpen}
          >
            {t("farms.sort.label", { defaultValue: "Sort" })}:{" "}
            <strong>{sortLabel}</strong>
            <FiChevronDown aria-hidden="true" />
          </D.SortButton>
          <AnimatePresence>
            {sortOpen ? (
              <D.SortPopover
                as={motion.div}
                role="listbox"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15 }}
              >
                {sortOptions.map((option) => (
                  <D.SortItem
                    key={option.value}
                    type="button"
                    $active={option.value === sortOption}
                    role="option"
                    aria-selected={option.value === sortOption}
                    onClick={() => {
                      onSortChange(option.value);
                      setSortOpen(false);
                    }}
                  >
                    {option.label}
                    {option.value === sortOption ? (
                      <FiCheck aria-hidden="true" />
                    ) : null}
                  </D.SortItem>
                ))}
              </D.SortPopover>
            ) : null}
          </AnimatePresence>
        </D.SortMenu>

        <D.ViewToggle
          role="radiogroup"
          aria-label={t("farms.view_toggle", { defaultValue: "View mode" })}
        >
          <D.ViewToggleBtn
            type="button"
            $active={viewMode === "grid"}
            role="radio"
            aria-checked={viewMode === "grid"}
            onClick={() => onViewModeChange("grid")}
          >
            <FiGrid aria-hidden="true" />
            {t("farms.grid_view", { defaultValue: "Grid" })}
          </D.ViewToggleBtn>
          <D.ViewToggleBtn
            type="button"
            $active={viewMode === "list"}
            role="radio"
            aria-checked={viewMode === "list"}
            onClick={() => onViewModeChange("list")}
          >
            <FiList aria-hidden="true" />
            {t("farms.list_view", { defaultValue: "List" })}
          </D.ViewToggleBtn>
        </D.ViewToggle>
      </D.CommandTopRow>

      <D.ChipRailWrap>
        <D.ChipRail role="group" aria-label={t("farms.filters.region.label", { defaultValue: "Region" })}>
          {quickRegions.map((option) => (
            <D.QuickChip
              key={option.value}
              type="button"
              $active={filters.region === option.value}
              aria-pressed={filters.region === option.value}
              onClick={() => onRegionSelect(option.value)}
            >
              {option.label}
            </D.QuickChip>
          ))}
          <D.QuickChip
            type="button"
            $tone="organic"
            $active={filters.organicOnly}
            aria-pressed={filters.organicOnly}
            onClick={() => onOrganicToggle(!filters.organicOnly)}
          >
            {filters.organicOnly ? <FiCheck aria-hidden="true" /> : null}
            {t("farms.filters.organic.label", { defaultValue: "Organic" })}
          </D.QuickChip>
        </D.ChipRail>
      </D.ChipRailWrap>

      <AnimatePresence>
        {activeFilterChips.length > 0 ? (
          <D.ActiveSummary
            as={motion.div}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <D.ActiveLabel>
              {t("farms.filters.active", { defaultValue: "Active filters" })}
            </D.ActiveLabel>
            {activeFilterChips.map((chip) => (
              <D.RemovableChip
                key={chip.id}
                type="button"
                onClick={() => onRemoveChip(chip)}
                aria-label={t("farms.filters.remove_chip", {
                  defaultValue: "Remove filter: {{label}}",
                  label: chip.label,
                })}
              >
                {chip.label}
                <FiX size={12} aria-hidden="true" />
              </D.RemovableChip>
            ))}
            <D.ResetAll type="button" onClick={onResetFilters}>
              {t("farms.filters.reset_all", { defaultValue: "Reset all" })}
            </D.ResetAll>
          </D.ActiveSummary>
        ) : null}
      </AnimatePresence>
    </D.CommandBar>
  );
};

export default FarmsCommandBar;
